import { PayloadAction } from '@reduxjs/toolkit';
import { quoteBuilderFieldValidators } from '@ui/features/quote-builder/state/fields';
import { WritableDraft } from 'immer';
import { z } from 'zod/v4';

import { QuoteBuilderStateType } from '../../types/state';
import { QuoteEditableNumberFields } from '../../types/state/quote';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';

/**
 * Updates an editable number field in the QuoteBuilderState and performs validation if a validator is provided.
 *
 * The string value is converted to a number via `Number(value)`, and then validated using the provided validator.
 *
 * Note that only the first validation error will be displayed in the UI if multiple errors occur.
 *
 * @param {WritableDraft<QuoteBuilderStateType>} state - The current state of the quote builder.
 * @param {PayloadAction<{field: QuoteEditableNumberFields; value: string; validator?: z.ZodNumber;}>} action - The action containing the field to update, its new value, and an optional validator.
 */
export function editableNumberFieldChanged(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<{
    field: QuoteEditableNumberFields;
    value: string;
  }>,
) {
  const { field, value } = action.payload;
  const validator = quoteBuilderFieldValidators[field];
  const elementId = buildElementId({
    uuid: state.uuid,
    field,
  });

  state.rawFieldValues[elementId] = value;

  let newValue = numberOrNull(value);

  const invalidIssueId = buildIssueId({
    area: 'quote',
    field,
    type: 'invalid',
  });

  if (validator) {
    const parsedValue = validator.safeParse(newValue);

    if (!parsedValue.success) {
      const error = z.flattenError(parsedValue.error).formErrors[0];

      upsertIssue(state, invalidIssueId, {
        title: 'Invalid Field',
        description: error,
        severity: 'error',
        area: 'quote',
        type: 'invalid',
        action: {
          type: 'modify',
          elementId,
        },
      });

      return;
    }

    newValue = parsedValue.data;
  }

  deleteIssue(state, invalidIssueId);

  state[field] = newValue;
}

function numberOrNull(value: string) {
  if (value.trim() === '') {
    return null;
  }

  const parsedValue = Number(value);
  return isNaN(parsedValue) ? null : parsedValue;
}
